import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  EMBED_PROTOCOL_VERSION,
  TICKET_EXCHANGE_PROTOCOL_VERSION,
  isBootstrapTicketMessage,
  type AuthenticationFailedMessage,
  type AuthenticationFailureCode,
  type AppReadyMessage,
  type ResizeMessage,
  type ScrollToTopMessage,
  type SessionEstablishedMessage,
  type SessionExpiredMessage,
} from "@loan-management/contracts";

import { createApplicationApi, type ApplicationApi } from "../api/applicationApi";

interface EmbeddedSessionMetadata {
  expiresAt: string;
  locationId: string | null;
  organizationId: string;
}

interface EmbeddedSessionContextValue {
  api: ApplicationApi | null;
  session: EmbeddedSessionMetadata | null;
  status: "standalone" | "waiting" | "authenticated" | "expired" | "error";
}

export const EmbeddedSessionContext = createContext<EmbeddedSessionContextValue>({
  api: null,
  session: null,
  status: "standalone",
});

export function EmbeddedSessionProvider({ children }: PropsWithChildren) {
  const parentOriginRef = useRef<string | null>(null);
  const expirationNotifiedRef = useRef(false);
  const [value, setValue] = useState<EmbeddedSessionContextValue>(() => ({
    api: null,
    session: null,
    status: window.parent === window ? "standalone" : "waiting",
  }));

  const expireSession = useCallback((correlationId: string) => {
    setValue({ api: null, session: null, status: "expired" });
    if (expirationNotifiedRef.current || !parentOriginRef.current || window.parent === window) {
      return;
    }

    expirationNotifiedRef.current = true;
    const expired: SessionExpiredMessage = {
      correlationId,
      protocolVersion: EMBED_PROTOCOL_VERSION,
      type: "LM_SESSION_EXPIRED",
    };
    window.parent.postMessage(expired, parentOriginRef.current);
  }, []);

  useEffect(() => {
    if (window.parent === window) {
      return;
    }

    const parentOrigin = resolveApprovedParentOrigin();
    const edgeFunctionOrigin = import.meta.env.VITE_EDGE_FUNCTION_ORIGIN;
    if (!parentOrigin || !edgeFunctionOrigin) {
      setValue({ api: null, session: null, status: "error" });
      return;
    }

    parentOriginRef.current = parentOrigin;
    let active = true;
    const handledRequestIds = new Set<string>();

    const reportAuthenticationFailure = (
      code: AuthenticationFailureCode,
      correlationId: string,
      requestId: string,
    ) => {
      if (!active) return;
      const failure: AuthenticationFailedMessage = {
        code,
        correlationId,
        protocolVersion: EMBED_PROTOCOL_VERSION,
        requestId,
        stage: "ticket-exchange",
        type: "LM_AUTHENTICATION_FAILED",
      };
      window.parent.postMessage(failure, parentOrigin);
      setValue({ api: null, session: null, status: "error" });
    };

    const onMessage = async (event: MessageEvent<unknown>) => {
      if (event.origin !== parentOrigin || event.source !== window.parent) {
        return;
      }
      if (!isBootstrapTicketMessage(event.data)) {
        return;
      }

      const { requestId, ticket } = event.data;
      if (handledRequestIds.has(requestId)) return;
      handledRequestIds.add(requestId);
      try {
        const response = await fetch(`${edgeFunctionOrigin}/exchange-bootstrap-ticket`, {
          body: JSON.stringify({
            protocolVersion: TICKET_EXCHANGE_PROTOCOL_VERSION,
            requestId,
            ticket,
          }),
          headers: { "Content-Type": "application/json" },
          method: "POST",
          mode: "cors",
          referrerPolicy: "strict-origin",
        });
        if (!response.ok) {
          const failure = await parseExchangeFailure(response);
          reportAuthenticationFailure(failure.code, failure.correlationId, requestId);
          return;
        }

        const exchangedSession = parseSession(await response.json());
        if (!active) return;
        expirationNotifiedRef.current = false;
        const session: EmbeddedSessionMetadata = {
          expiresAt: exchangedSession.expiresAt,
          locationId: exchangedSession.locationId,
          organizationId: exchangedSession.organizationId,
        };
        const api = createApplicationApi({
          accessToken: exchangedSession.accessToken,
          baseUrl: edgeFunctionOrigin,
          onSessionExpired: expireSession,
        });
        setValue({ api, session, status: "authenticated" });

        const established: SessionEstablishedMessage = {
          protocolVersion: EMBED_PROTOCOL_VERSION,
          requestId,
          type: "LM_SESSION_ESTABLISHED",
        };
        window.parent.postMessage(established, parentOrigin);
      } catch {
        reportAuthenticationFailure("TICKET_EXCHANGE_FAILED", crypto.randomUUID(), requestId);
      }
    };

    window.addEventListener("message", onMessage);
    const ready: AppReadyMessage = {
      protocolVersion: EMBED_PROTOCOL_VERSION,
      type: "LM_APP_READY",
    };
    window.parent.postMessage(ready, parentOrigin);

    const publishHeight = () => {
      const main=document.getElementById("main-content");
      const contentHeight=main
        ? Math.ceil(main.getBoundingClientRect().bottom+window.scrollY)
        : Math.ceil(document.body.scrollHeight);
      const resize: ResizeMessage = {
        height: contentHeight,
        protocolVersion: EMBED_PROTOCOL_VERSION,
        type: "LM_RESIZE",
      };
      window.parent.postMessage(resize, parentOrigin);
    };
    const publishHeightAfterLayout = () => requestAnimationFrame(publishHeight);
    const publishLayoutChange = () => {
      publishHeightAfterLayout();
      const scrollMessage:ScrollToTopMessage={protocolVersion:EMBED_PROTOCOL_VERSION,type:"LM_SCROLL_TO_TOP"};
      window.parent.postMessage(scrollMessage,parentOrigin);
    };
    const resizeObserver = new ResizeObserver(publishHeightAfterLayout);
    resizeObserver.observe(document.body);
    window.addEventListener("loan-management:layout-change",publishLayoutChange);
    publishHeightAfterLayout();

    return () => {
      active = false;
      parentOriginRef.current = null;
      resizeObserver.disconnect();
      window.removeEventListener("loan-management:layout-change",publishLayoutChange);
      window.removeEventListener("message", onMessage);
    };
  }, [expireSession]);

  return <EmbeddedSessionContext.Provider value={value}>{children}</EmbeddedSessionContext.Provider>;
}

function resolveApprovedParentOrigin(): string | null {
  if (!document.referrer) return null;

  try {
    const referrer = new URL(document.referrer);
    const isIntacctHost =
      referrer.hostname === "intacct.com" || referrer.hostname.endsWith(".intacct.com");
    return referrer.protocol === "https:" && isIntacctHost ? referrer.origin : null;
  } catch {
    return null;
  }
}

function parseSession(value: unknown): EmbeddedSessionMetadata & { accessToken: string } {
  if (typeof value !== "object" || value === null) {
    throw new Error("Invalid session response");
  }

  const session = value as Record<string, unknown>;
  if (
    typeof session.accessToken !== "string" ||
    typeof session.expiresAt !== "string" ||
    typeof session.organizationId !== "string" ||
    (session.locationId !== null && typeof session.locationId !== "string")
  ) {
    throw new Error("Invalid session response");
  }

  return {
    accessToken: session.accessToken,
    expiresAt: session.expiresAt,
    locationId: session.locationId,
    organizationId: session.organizationId,
  };
}

async function parseExchangeFailure(response: Response): Promise<{
  code: AuthenticationFailureCode;
  correlationId: string;
}> {
  let value: unknown;
  try {
    value = await response.json();
  } catch {
    value = null;
  }

  const failure = typeof value === "object" && value !== null
    ? value as Record<string, unknown>
    : {};
  return {
    code: response.status === 401 && failure.code === "TICKET_INVALID"
      ? "TICKET_INVALID"
      : "TICKET_EXCHANGE_FAILED",
    correlationId: typeof failure.correlationId === "string" &&
      failure.correlationId.length > 0 &&
      failure.correlationId.length <= 100
      ? failure.correlationId
      : crypto.randomUUID(),
  };
}
